import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { buildCustomHeaders } from '@deighton/shared-ui/core';
import { LICENSING_ACCESS_TOKEN_KEY } from './licensing-auth.service';
import { SessionExpiredService } from './session-expired.service';

/**
 * Functional HTTP interceptor that attaches authentication headers to every
 * outgoing request:
 *
 * - `Authorization: Bearer <access_token>` — Okta access token for JWT validation,
 *   read from localStorage where {@link LicensingAuthService} writes it after a
 *   successful PKCE exchange (and keeps in sync with auto-renewal).
 * - `X-Deighton-User-Context: <jwt>` — Signed user context JWT used by the
 *   backend to resolve tenant/environment in cross-origin (incognito) mode.
 *   Read by {@link buildCustomHeaders} from `localStorage['dtims_user_context_token']`.
 *
 * On HTTP 401 Unauthorized only, marks the session as expired so pages can prompt
 * for re-login. Other status codes (403, 5xx, etc.) are left to callers.
 *
 * Mirrors the shell-app `authInterceptor` shape exactly so the two apps stay
 * easy to compare. The only structural difference is the token source:
 * shell-app reads from `OKTA_AUTH.getAccessToken()` because it bootstraps
 * `OktaAuthModule.forRoot()` at app start; licensing reads from localStorage
 * because the tenant-specific Okta config is only known after the backend
 * callback, so a single bootstrap `OktaAuth` instance isn't applicable here.
 *
 * Register with `provideHttpClient(withInterceptors([authInterceptor]))`.
 *
 * TODO(de-dup): The body of this interceptor is functionally identical to
 * `apps/shell-app/src/services/auth.interceptor.ts`. When both apps converge
 * on a single token-storage strategy, lift this into `@deighton/shared-ui`.
 */
export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const sessionExpiredService: SessionExpiredService = inject(SessionExpiredService);

  const accessToken: string | undefined =
    typeof localStorage !== 'undefined'
      ? (localStorage.getItem(LICENSING_ACCESS_TOKEN_KEY) ?? undefined)
      : undefined;

  const customHeaders: Record<string, string> = buildCustomHeaders(accessToken);

  const send = (request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> =>
    next(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          sessionExpiredService.markSessionExpired();
        }

        return throwError(() => error);
      })
    );

  if (Object.keys(customHeaders).length > 0) {
    return send(req.clone({ setHeaders: customHeaders }));
  }

  return send(req);
}
